import React, { useEffect, useRef, useState } from 'react'

import clsx from 'clsx'
import ReactDOM from 'react-dom'

import Paper from 'components/Paper'
import Typography from 'components/Typography'
import detachTask from 'helpers/detachTask'
import getScrollbarSize from 'helpers/getScrollbarSize'
import { useIsomorphicLayoutEffect, useTransition } from 'hooks'
import useOutsideClick from 'hooks/useOutsideClick'
import useViewport from 'hooks/useViewport'

import { calculateListPosition } from './helpers'

import classes from './Select.module.scss'

const getItemValue = item => item?.dataset?.value || item?.props?.value

const Select = ({
  children,
  value,
  placeHolder = '',
  className,
  onChange,
  placeHolderClasses,
  IconComponent,
  disabled = false,
  onClick,
  ...other
}) => {
  const findItemByValue = itemValue =>
    React.Children.map(children, child => child).find(child => getItemValue(child) === itemValue)

  const getItemIndex = itemValue => React.Children.map(children, child => child && child.props.value).indexOf(itemValue)

  const containerInputRef = useRef()
  const inputRef = useRef()
  const listRef = useRef()
  const [isOpen, setIsOpen] = useState(false)
  const [currentItem, setCurrentItem] = useState(findItemByValue(value))
  const [currentIndex, setCurrentIndex] = useState(null)
  const { down } = useViewport()
  const { shouldMount, stage } = useTransition(isOpen, 100)

  useEffect(() => {
    if (isOpen) {
      document.body.style.paddingRight = `${getScrollbarSize()}px`
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.body.style.paddingRight = '0'
      document.body.style.overflow = 'initial'
    }
  }, [isOpen])

  useEffect(() => {
    setCurrentItem(findItemByValue(value))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const onClickPlaceHolder = event => {
    inputRef.current.focus()
    setCurrentIndex(getItemIndex(getItemValue(currentItem)))
    setIsOpen(true)

    if (onClick) {
      detachTask(onClick, event)
    }
  }

  const closeDropDown = () => {
    setIsOpen(false)
    inputRef.current.blur()
  }

  const onHandleChange = e => {
    closeDropDown()
    setCurrentItem(findItemByValue(getItemValue(e.currentTarget)))
    if (onChange) detachTask(onChange, getItemValue(e.currentTarget))
  }

  useIsomorphicLayoutEffect(() => {
    if (!shouldMount || !listRef.current) {
      return
    }

    const selectContainerRect = containerInputRef.current.getBoundingClientRect()
    const { top, bottom, left } = calculateListPosition(listRef, selectContainerRect)

    listRef.current.style.top = top
    listRef.current.style.left = `${left}px`
    listRef.current.style.bottom = bottom
  }, [shouldMount, listRef])

  useOutsideClick(listRef, closeDropDown)

  const scrollTo = scrollDistance => {
    listRef.current.scrollTo({
      top: scrollDistance,
      behavior: 'smooth',
    })
  }

  const handleArrowKeyDown = (predicate, scrollDistance, index, startIndex, items) => {
    if (items.length) {
      if (predicate) {
        scrollTo(scrollDistance)
        return setCurrentIndex(startIndex)
      }
      items[index].scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'smooth' })
      setCurrentIndex(index)
    }
  }

  const onKeyDownInput = event => {
    const list = listRef.current
    if (!list) {
      return
    }

    const items = list.firstElementChild.querySelectorAll('[data-listitem]')

    event.preventDefault()

    switch (event.key) {
      case 'ArrowUp':
        return handleArrowKeyDown(
          !currentIndex,
          listRef.current.scrollHeight,
          currentIndex - 1,
          items.length - 1,
          items
        )
      case 'ArrowDown':
        return handleArrowKeyDown(
          currentIndex === null || currentIndex === items.length - 1,
          0,
          currentIndex + 1,
          0,
          items
        )
      case 'Enter':
        const itemValue = getItemValue(items[currentIndex])
        setCurrentItem(findItemByValue(itemValue))
        onChange(itemValue)

      case 'Escape':
        closeDropDown()
        break
    }
  }

  return (
    <div className={clsx('Select', classes.root, className)} {...other}>
      <div
        ref={containerInputRef}
        role="presentation"
        className={clsx(classes.placeHolder, disabled && classes.disabled, placeHolderClasses)}
        onClick={onClickPlaceHolder}
        data-testid="select-placeholder"
      >
        <input
          ref={inputRef}
          value={value}
          aria-label={value}
          onChange={onChange}
          onKeyDown={onKeyDownInput}
          {...(down('sm') ? { type: 'hidden' } : {})}
        />
        <Typography component="span" className={classes.placeholderText}>
          {currentItem?.props.children || placeHolder}
        </Typography>
        {IconComponent && <IconComponent />}
      </div>

      {shouldMount &&
        ReactDOM.createPortal(
          <Paper
            onMouseDown={e => e.preventDefault()}
            className={clsx(classes.popper, classes[stage])}
            role="presentation"
            ref={listRef}
            style={{ minWidth: `${containerInputRef?.current?.scrollWidth}px` }}
          >
            <ul data-testid="select-item-list" className={classes.list}>
              {React.Children.map(
                children,
                (child, i) =>
                  child &&
                  React.cloneElement(child, {
                    'data-listitem': i,
                    onClick: onHandleChange,
                    selected:
                      child.props.value === currentItem.props.value || currentIndex === getItemIndex(child.props.value),
                  })
              )}
            </ul>
          </Paper>,
          document?.getElementById('portal_root') || document.body
        )}
    </div>
  )
}

export default Select
